import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, PlayCircle, Save, Zap } from "lucide-react";
import { api, apiError, logError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  ["super_admin", "Owner"],
  ["director", "Director"],
  ["sales_manager", "Sales Manager"],
  ["purchase_manager", "Purchase Manager"],
  ["store_manager", "Store Manager"],
  ["production_manager", "Production Manager"],
  ["accounts_manager", "Accounts Manager"],
  ["project_manager", "Project Manager"],
];

const DailyBrief = () => {
  const [conf, setConf] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/brief/settings")
      .then(({ data }) => setConf(data))
      .catch((e) => logError("daily brief settings", e));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!conf) return null;

  const save = async (changes) => {
    setBusy("save");
    try {
      const next = { ...conf, ...changes };
      const { data } = await api.put("/brief/settings", {
        enabled: next.enabled,
        recipients: (next.recipients || []).filter(Boolean),
        whatsapp_numbers: (next.whatsapp_numbers || []).filter(Boolean),
      });
      setConf(data);
      toast.success("Daily brief settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const sendNow = async () => {
    setBusy("send");
    try {
      const { data } = await api.post("/brief/send");
      toast.success(
        data.emails_sent
          ? `Brief emailed to ${data.emails_sent} recipient(s)`
          : data.detail || "Brief prepared, but nothing could be delivered"
      );
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const showPreview = async () => {
    setBusy("preview");
    try {
      const { data } = await api.get("/brief/preview");
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="daily-brief-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            <span className="font-medium">Daily Owner Brief — 8:00 AM IST</span>
            {conf.enabled && <Badge variant="outline" className="text-[10px]">on</Badge>}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Attendance, pending approvals, overdue money and low stock — one email every morning.
            {!conf.email_available && " Email provider is not configured yet."}
            {conf.whatsapp_available
              ? " WhatsApp delivery is available."
              : " WhatsApp stays dormant until a provider is connected."}
          </p>
        </div>
        <Switch
          data-testid="daily-brief-toggle"
          checked={conf.enabled}
          onCheckedChange={(v) => save({ enabled: v })}
        />
      </div>

      <div className="mt-3 grid gap-3 border-t border-border pt-3 sm:grid-cols-2">
        <div>
          <div className="overline mb-1">Email recipients (comma separated)</div>
          <Input
            data-testid="daily-brief-recipients"
            placeholder="Leave blank to use every Owner and Director account"
            value={(conf.recipients || []).join(", ")}
            onChange={(e) =>
              setConf({ ...conf, recipients: e.target.value.split(",").map((x) => x.trim()) })
            }
          />
        </div>
        <div>
          <div className="overline mb-1">WhatsApp numbers (comma separated)</div>
          <Input
            data-testid="daily-brief-whatsapp"
            placeholder="+91…"
            value={(conf.whatsapp_numbers || []).join(", ")}
            onChange={(e) =>
              setConf({ ...conf, whatsapp_numbers: e.target.value.split(",").map((x) => x.trim()) })
            }
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" onClick={() => save({})} disabled={busy === "save"}
          data-testid="daily-brief-save" className="gap-1.5">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={showPreview} disabled={busy === "preview"}
          data-testid="daily-brief-preview">
          Preview today's brief
        </Button>
        <Button size="sm" variant="outline" onClick={sendNow} disabled={busy === "send"}
          data-testid="daily-brief-send">
          {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send now"}
        </Button>
      </div>

      {preview && (
        <div className="mt-3 space-y-2 border-t border-border pt-3" data-testid="daily-brief-preview-box">
          <div className="text-sm font-medium">{preview.headline}</div>
          {preview.sections.map((s) => (
            <div key={s.title}>
              <div className="overline">{s.title}</div>
              {s.lines.map((l) => (
                <div key={l} className="text-xs text-muted-foreground">• {l}</div>
              ))}
            </div>
          ))}
        </div>
      )}

      {conf.recent_runs?.length > 0 && (
        <div className="mt-3 text-xs text-muted-foreground" data-testid="daily-brief-runs">
          Last sent: {conf.recent_runs[0].date} — {conf.recent_runs[0].emails_sent} email(s)
          {conf.recent_runs[0].detail ? ` — ${conf.recent_runs[0].detail}` : ""}
        </div>
      )}
    </div>
  );
};

export default function Automation() {
  const [rules, setRules] = useState([]);
  const [emailAvailable, setEmailAvailable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const load = useCallback(() => {
    api.get("/automation/rules")
      .then(({ data }) => {
        setRules(data.rules);
        setEmailAvailable(data.email_available);
      })
      .catch((e) => logError("automation rules", e));
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = (code, changes) =>
    setRules((rs) => rs.map((r) => (r.code === code ? { ...r, ...changes } : r)));

  const toggleRole = (rule, role) =>
    patch(rule.code, {
      notify_roles: rule.notify_roles.includes(role)
        ? rule.notify_roles.filter((r) => r !== role)
        : [...rule.notify_roles, role],
    });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/automation/rules", {
        rules: rules.map(({ code, enabled, threshold, notify_roles, send_email }) => ({
          code, enabled, threshold: Number(threshold), notify_roles, send_email,
        })),
      });
      setRules(data.rules);
      toast.success("Automation rules saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/automation/run");
      setLastRun(data);
      const hits = data.results.reduce((a, r) => a + r.matches, 0);
      toast.success(hits ? `${hits} thing(s) need attention` : "All clear — nothing to alert");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <PageHeader
        testId="automation-header"
        title="Automation rules"
        subtitle="When something needs attention, Pramsia tells the right people automatically."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" data-testid="automation-run-button"
              onClick={runNow} disabled={running}>
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
              Run checks now
            </Button>
            <Button className="gap-2" data-testid="automation-save-button" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save rules
            </Button>
          </div>
        }
      />

      <div className="space-y-3 p-4" data-testid="automation-rule-list">
        <DailyBrief />
        {rules.map((rule) => (
          <div key={rule.code} data-testid={`rule-${rule.code}`}
            className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" />
                  <span className="font-medium">{rule.title}</span>
                  {rule.enabled && <Badge variant="outline" className="text-[10px]">on</Badge>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{rule.description}</p>
              </div>
              <Switch
                data-testid={`rule-toggle-${rule.code}`}
                checked={rule.enabled}
                onCheckedChange={(v) => patch(rule.code, { enabled: v })}
              />
            </div>

            {rule.enabled && (
              <div className="mt-3 space-y-3 border-t border-border pt-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">{rule.threshold_label}</span>
                  <Input
                    type="number"
                    className="h-8 w-24"
                    data-testid={`rule-threshold-${rule.code}`}
                    value={rule.threshold}
                    onChange={(e) => patch(rule.code, { threshold: e.target.value })}
                  />
                </div>
                <div>
                  <div className="overline mb-1">Alert these people</div>
                  <div className="flex flex-wrap gap-1.5">
                    {ROLE_OPTIONS.map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        data-testid={`rule-${rule.code}-role-${value}`}
                        onClick={() => toggleRole(rule, value)}
                        className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                          rule.notify_roles.includes(value)
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border hover:bg-secondary"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Switch
                    data-testid={`rule-email-${rule.code}`}
                    checked={rule.send_email}
                    disabled={!emailAvailable}
                    onCheckedChange={(v) => patch(rule.code, { send_email: v })}
                  />
                  Also send an email
                  {!emailAvailable && " (email is not configured yet)"}
                </label>
              </div>
            )}
          </div>
        ))}
      </div>

      {lastRun && (
        <div className="p-4" data-testid="automation-last-run">
          <div className="overline mb-2">Last check</div>
          {lastRun.results.map((r) => (
            <div key={r.code} className="border-b border-border py-2 text-sm last:border-0">
              <span className="font-medium">{r.title}</span>
              <span className="ml-2 text-muted-foreground">{r.matches} match(es)</span>
              {r.samples.map((s) => (
                <div key={s} className="text-xs text-muted-foreground">• {s}</div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
